// frontend/src/components/admin/PreferenceDefinitions.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Download, 
  Database,
  Check,
  X
} from 'lucide-react';
import ConfirmDialogShell from '../dialogs/ConfirmDialogShell';

/**
 * PreferenceDefinitions Component
 * Manage system-wide preference definitions (CRUD operations)
 */
const PreferenceDefinitions = ({ userRole }) => {
  const { t } = useTranslation();
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preferenceTypes, setPreferenceTypes] = useState([]);
  const [dataTypes, setDataTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRequiredRole, setFilterRequiredRole] = useState('all');
  const [filterDataType, setFilterDataType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    preference_key: '',
    preference_type: 'user_global',
    data_type: 'string',
    default_value: '',
    description: '',
    category: '',
    display_order: '',
    is_active: true,
    is_translatable: false,
    required_role: null,
    validation_rules: {},
    allowed_values: []
  });
  const entityId = localStorage.getItem('entityId');

  useEffect(() => {
    fetchDefinitions();
    fetchPreferenceTypes();
    fetchDataTypes();
  }, []);

  const fetchDefinitions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/admin/preferences/${entityId}/definitions`);
      if (response.data.success) {
        setDefinitions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching preference definitions:', error);
      toast.error(t('errorFetchingPreferenceDefinitions'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferenceTypes = async () => {
    try {
      const response = await axios.get(`/api/admin/preferences/${entityId}/types`);
      if (response.data.success) {
        setPreferenceTypes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching preference types:', error);
      // Fallback to hardcoded types if API fails
      setPreferenceTypes(['user_global', 'user_entity', 'entity', 'system']);
    }
  };

  const fetchDataTypes = async () => {
    try {
      // For now, keep data types static as they're defined in application logic
      // If needed, these could also be made dynamic in the future
      setDataTypes(['string', 'integer', 'boolean', 'json', 'enum', 'reference']);
    } catch (error) {
      console.error('Error setting data types:', error);
      setDataTypes(['string', 'integer', 'boolean', 'json', 'enum', 'reference']);
    }
  };

  const handleCreate = async () => {
    try {
      const parsedDisplayOrder = formData.display_order === '' ? 0 : parseInt(formData.display_order, 10);
      const payload = {
        ...formData,
        display_order: Number.isNaN(parsedDisplayOrder) ? 0 : parsedDisplayOrder,
        validation_rules: Object.keys(formData.validation_rules).length > 0 ? formData.validation_rules : null,
        allowed_values: formData.allowed_values.length > 0 ? formData.allowed_values : null
      };

      const response = await axios.post(`/api/admin/preferences/${entityId}/definitions`, payload);
      
      if (response.data.success) {
        toast.success(t('preferenceDefinitionCreated'));
        setShowCreateModal(false);
        resetForm();
        fetchDefinitions();
      }
    } catch (error) {
      console.error('Error creating preference definition:', error);
      toast.error(error.response?.data?.error || t('errorCreatingPreferenceDefinition'));
    }
  };

  const handleUpdate = async () => {
    try {
      const parsedDisplayOrder = formData.display_order === '' ? 0 : parseInt(formData.display_order, 10);
      const payload = {
        ...formData,
        display_order: Number.isNaN(parsedDisplayOrder) ? 0 : parsedDisplayOrder,
        validation_rules: Object.keys(formData.validation_rules).length > 0 ? formData.validation_rules : null,
        allowed_values: formData.allowed_values.length > 0 ? formData.allowed_values : null
      };

      const response = await axios.put(
        `/api/admin/preferences/${entityId}/definitions/${editingDefinition.id}`,
        payload
      );
      
      if (response.data.success) {
        toast.success(t('preferenceDefinitionUpdated'));
        setEditingDefinition(null);
        resetForm();
        fetchDefinitions();
      }
    } catch (error) {
      console.error('Error updating preference definition:', error);
      toast.error(error.response?.data?.error || t('errorUpdatingPreferenceDefinition'));
    }
  };

  const handleDelete = (definition) => {
    setDeleteConfirmation({
      definition,
      force: false,
      message: t('confirmDeletePreferenceDefinition', { key: definition.preference_key })
    });
  };

  const confirmDeleteDefinition = async () => {
    if (!deleteConfirmation?.definition) return;
    const { definition, force } = deleteConfirmation;
    try {
      setDeleteLoading(true);
      const forceQuery = force ? '?force=true' : '';
      const response = await axios.delete(`/api/admin/preferences/${entityId}/definitions/${definition.id}${forceQuery}`);
      
      if (response.data.success) {
        toast.success(force ? t('preferenceDefinitionDeletedForce') : t('preferenceDefinitionDeleted'));
        setDeleteConfirmation(null);
        fetchDefinitions();
      }
    } catch (error) {
      console.error('Error deleting preference definition:', error);
      if (!force && error.response?.status === 400) {
        setDeleteConfirmation({
          definition,
          force: true,
          message: t('preferenceInUseForceDelete', {
            key: definition.preference_key,
            count: error.response.data.inUseCount
          })
        });
      } else {
        toast.error(error.response?.data?.error || t('errorDeletingPreferenceDefinition'));
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      preference_key: '',
      preference_type: 'user_global',
      data_type: 'string',
      default_value: '',
      description: '',
      category: '',
      display_order: '',
      is_active: true,
      is_translatable: false,
      required_role: null,
      validation_rules: {},
      allowed_values: []
    });
  };

  const startEdit = (definition) => {
    setEditingDefinition(definition);
    
    // Helper function to safely parse JSON
    const safeJsonParse = (value, defaultValue = {}) => {
      if (!value) return defaultValue;
      
      // If it's already an object, return it
      if (typeof value === 'object') return value;
      
      // If it's a string, try to parse it
      if (typeof value === 'string') {
        try {
          return JSON.parse(value);
        } catch (error) {
          console.warn('Failed to parse JSON:', value, error);
          return defaultValue;
        }
      }
      
      return defaultValue;
    };

    setFormData({
      preference_key: definition.preference_key,
      preference_type: definition.preference_type,
      data_type: definition.data_type,
      default_value: definition.default_value || '',
      description: definition.description || '',
      category: definition.category || '',
      display_order: definition.display_order !== undefined && definition.display_order !== null
        ? String(definition.display_order)
        : '',
      is_active: Boolean(definition.is_active),
      is_translatable: Boolean(definition.is_translatable),
      required_role: definition.required_role,
      validation_rules: safeJsonParse(definition.validation_rules, {}),
      allowed_values: safeJsonParse(definition.allowed_values, [])
    });
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`/api/admin/preferences/${entityId}/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { 
        type: 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `preference_definitions_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t('preferencesExported'));
    } catch (error) {
      console.error('Error exporting preferences:', error);
      toast.error(t('errorExportingPreferences'));
    }
  };

  // Filter definitions based on search and filters
  const filteredDefinitions = definitions.filter(def => {
    const matchesSearch = def.preference_key.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (def.description && def.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = filterType === 'all' || def.preference_type === filterType;
    const matchesCategory = filterCategory === 'all' || def.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && def.is_active) ||
                         (filterStatus === 'inactive' && !def.is_active);
    const matchesRequiredRole = filterRequiredRole === 'all' || 
                               (filterRequiredRole === 'null' && !def.required_role) ||
                               def.required_role === filterRequiredRole;
    const matchesDataType = filterDataType === 'all' || def.data_type === filterDataType;
    
    return matchesSearch && matchesType && matchesCategory && matchesStatus && matchesRequiredRole && matchesDataType;
  });

  // Get unique categories and required roles for filter dropdowns
  const categories = [...new Set(definitions.map(def => def.category).filter(Boolean))];
  const requiredRoles = [...new Set(definitions.map(def => def.required_role).filter(Boolean))];
  const definitionDataTypes = [...new Set(definitions.map(def => def.data_type).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            <Database className="inline w-5 h-5 mr-2" />
            {t('preferenceDefinitions')} ({filteredDefinitions.length}/{definitions.length})
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('manageSystemPreferenceDefinitions')}
          </p>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={handleExport}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
          >
            <Download className="w-4 h-4 mr-2" />
            {t('export')}
          </button>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('createDefinition')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('searchDefinitions')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="all">{t('allTypes')}</option>
          {preferenceTypes.map(type => (
            <option key={type} value={type}>
              {t(type.replace('_', ''))}
            </option>
          ))}
        </select>
        
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="all">{t('allCategories')}</option>
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="all">{t('allStatuses')}</option>
          <option value="active">{t('active')}</option>
          <option value="inactive">{t('inactive')}</option>
        </select>
        
        <select
          value={filterRequiredRole}
          onChange={(e) => setFilterRequiredRole(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="all">{t('allRequiredRoles') || 'All Required Roles'}</option>
          {requiredRoles.map(role => (
            <option key={role} value={role}>{role}</option>
          ))}
          <option key="null" value="null">{t('isNull')}</option>
        </select>
        
        <select
          value={filterDataType}
          onChange={(e) => setFilterDataType(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="all">{t('allDataTypes')}</option>
          {definitionDataTypes.map(dataType => (
            <option key={dataType} value={dataType}>{dataType}</option>
          ))}
        </select>
      </div>

      {/* Definitions Table */}
      <div className="overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg" style={{maxHeight: '600px'}}>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-800 sticky left-0 z-20 border-r border-gray-200 dark:border-gray-700">
                {t('actions')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('preferenceKey')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('type')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('requiredRole')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('dataType')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('translatable')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('defaultValue')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('category')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('status')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredDefinitions.map((definition) => (
              <tr key={definition.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 group">
                <td className="px-4 py-4 whitespace-nowrap bg-white dark:bg-gray-900 group-hover:bg-gray-50 dark:group-hover:bg-gray-800 sticky left-0 z-10 border-r border-gray-200 dark:border-gray-700">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => startEdit(definition)}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      title={t('edit')}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(definition)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                      title={t('delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {definition.preference_key}
                    </div>
                    {definition.description && (
                      <div className="text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                        {definition.description}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    definition.preference_type === 'user_global' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300' :
                    definition.preference_type === 'user_entity' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' :
                    'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
                  }`}>
                    {definition.preference_type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  <span className="inline-flex px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                    {definition.required_role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  <span className="inline-flex px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                    {definition.data_type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  <span className="inline-flex px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                    {definition.is_translatable}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs max-w-32 inline-block truncate">
                    {definition.default_value || 'null'}
                  </code>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  <span className="inline-flex px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                    {definition.category || '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {definition.is_active ? (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300 rounded-full">
                      <Check className="w-3 h-3 mr-1" />
                      {t('active')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded-full">
                      <X className="w-3 h-3 mr-1" />
                      {t('inactive')}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredDefinitions.length === 0 && (
        <div className="text-center py-8">
          <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            {searchTerm || filterType !== 'all' || filterCategory !== 'all' || filterStatus !== 'all' || filterDataType !== 'all'
              ? t('noDefinitionsMatchFilter') 
              : t('noDefinitionsFound')
            }
          </p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingDefinition) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              {editingDefinition ? t('editDefinition') : t('createDefinition')}
            </h3>
            
            <form onSubmit={(e) => { e.preventDefault(); editingDefinition ? handleUpdate() : handleCreate(); }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('preferenceKey')}
                  </label>
                  <input
                    type="text"
                    value={formData.preference_key}
                    onChange={(e) => setFormData({...formData, preference_key: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('type')}
                  </label>
                  <select
                    value={formData.preference_type}
                    onChange={(e) => setFormData({...formData, preference_type: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    {preferenceTypes.map(type => (
                      <option key={type} value={type}>
                        {t(type.replace('_', ''))}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('dataType')}
                  </label>
                  <select
                    value={formData.data_type}
                    onChange={(e) => setFormData({...formData, data_type: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    {dataTypes.map(type => (
                      <option key={type} value={type}>
                        {type === 'json' ? 'JSON' : type === 'enum' ? 'Enum' : type === 'reference' ? 'Reference' : t(type)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_translatable}
                      onChange={(e) => setFormData({...formData, is_translatable: e.target.checked})}
                      disabled={formData.data_type !== 'string'}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className={`ml-2 text-sm ${formData.data_type !== 'string' ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300'}`}>
                      {t('isTranslatable')}
                    </span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('isTranslatableDescription')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('defaultValue')}
                  </label>
                  <input
                    type="text"
                    value={formData.default_value}
                    onChange={(e) => setFormData({...formData, default_value: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('description')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows="2"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('category')}
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="e.g., display, notifications, security"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('displayOrder')}
                    </label>
                    <input
                      type="number"
                      value={formData.display_order}
                      onChange={(e) => {
                        const { value } = e.target;
                        // Strip leading zeros but keep the raw string so users can clear the field while editing
                        const sanitized = value === '' ? '' : value.replace(/^0+(?=\d)/, '');
                        setFormData({ ...formData, display_order: sanitized });
                      }}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('requiredRole')}
                    </label>
                    <select
                      value={formData.required_role || ''}
                      onChange={(e) => setFormData({...formData, required_role: e.target.value || null})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="">{t('none')}</option>
                      <option value="user">User</option>
                      <option value="driver">Driver</option>
                      <option value="admin">Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('allowedValues')}
                  </label>
                  <textarea
                    value={Array.isArray(formData.allowed_values) ? formData.allowed_values.join('\n') : ''}
                    onChange={(e) => {
                      // Split by newline or comma, strip surrounding quotes and whitespace
                      const values = e.target.value.split(/[\n,]/)
                        .map(v => v.trim().replace(/^["']|["']$/g, ''))
                        .filter(v => v);
                      setFormData({...formData, allowed_values: values});
                    }}
                    rows="3"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Enter allowed values, one per line (for enum type)"
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('oneValuePerLine')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('validationRules')}
                  </label>
                  <textarea
                    value={typeof formData.validation_rules === 'object' ? JSON.stringify(formData.validation_rules, null, 2) : formData.validation_rules}
                    onChange={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        setFormData({...formData, validation_rules: parsed});
                      } catch {
                        // Keep the raw string while user is typing
                        setFormData({...formData, validation_rules: e.target.value});
                      }
                    }}
                    rows="3"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm"
                    placeholder='{"min": 0, "max": 100, "required": true}'
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('validationRulesJson')}</p>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('active')}</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingDefinition(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  {editingDefinition ? t('update') : t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialogShell
        isOpen={!!deleteConfirmation}
        onClose={() => !deleteLoading && setDeleteConfirmation(null)}
        onConfirm={confirmDeleteDefinition}
        title={deleteConfirmation?.force ? t('confirmDelete') : t('delete')}
        confirmLabel={deleteConfirmation?.force ? t('confirm') : t('delete')}
        confirmVariant="danger"
        confirmIcon={Trash2}
        isLoading={deleteLoading}
        isDisabled={deleteLoading}
        showIcon={true}
        iconVariant="danger"
        closeOnBackdropClick={!deleteLoading}
      >
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {deleteConfirmation?.message}
        </p>
      </ConfirmDialogShell>
    </div>
  );
};

export default PreferenceDefinitions;
